import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth, loginWithGoogle, logoutUser, onAuthStateChanged } from '../services/firebase';

export interface AppUser {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  isDemo?: boolean;
}

export type RequiredAuthAction = 'workspace' | 'inviteNotetaker' | null;

interface AuthContextType {
  user: AppUser | null;
  firebaseUser: User | null;
  loading: boolean;
  error: string | null;
  signInModalOpen: boolean;
  targetAction: RequiredAuthAction;
  openSignInModal: (action?: RequiredAuthAction) => void;
  closeSignInModal: () => void;
  signInWithGoogle: () => Promise<boolean>;
  signOut: () => Promise<void>;
  loginAsDemoUser: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEMO_USER: AppUser = {
  uid: 'demo-user-123',
  displayName: 'Akhil PM',
  email: 'akhil.pm@scrumai.io',
  photoURL: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=256&q=80',
  isDemo: true
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [signInModalOpen, setSignInModalOpen] = useState<boolean>(false);
  const [targetAction, setTargetAction] = useState<RequiredAuthAction>(null);

  useEffect(() => {
    // Clear any previous stale demo user so fresh visitors start logged out
    localStorage.removeItem('scrumai_demo_user');

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser);
        setUser({
          uid: fbUser.uid,
          displayName: fbUser.displayName || 'Scrum Team Member',
          email: fbUser.email,
          photoURL: fbUser.photoURL || null,
          isDemo: false
        });
      } else {
        setFirebaseUser(null);
        setUser(null);
      }
      setLoading(false);
    }, (err) => {
      console.error('Firebase Auth State Error:', err);
      setError(err.message);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const openSignInModal = (action?: RequiredAuthAction) => {
    setTargetAction(action || null);
    setSignInModalOpen(true);
  };

  const closeSignInModal = () => {
    setSignInModalOpen(false);
    setTargetAction(null);
  };

  const handleGoogleSignIn = async (): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      const fbUser = await loginWithGoogle();
      setUser({
        uid: fbUser.uid,
        displayName: fbUser.displayName || 'Scrum Team Member',
        email: fbUser.email,
        photoURL: fbUser.photoURL || null,
        isDemo: false
      });
      closeSignInModal();
      
      return true;
    } catch (err: any) {
      console.error('Google Sign In Error:', err);
      let message = 'Failed to sign in with Google.';
      if (err.code === 'auth/popup-closed-by-user') {
        message = 'Sign in was cancelled (popup closed).';
      } else if (err.code === 'auth/unauthorized-domain') {
        message = 'This domain is not authorized in Firebase Console. You can also explore in Demo Mode!';
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setLoading(true);
      localStorage.removeItem('scrumai_demo_user');
      await logoutUser();
      setUser(null);
      setFirebaseUser(null);
    } catch (err: any) {
      console.error('Sign out error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loginAsDemoUser = () => {
    localStorage.setItem('scrumai_demo_user', JSON.stringify(DEMO_USER));
    setUser(DEMO_USER);
    closeSignInModal();
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        firebaseUser,
        loading,
        error,
        signInModalOpen,
        targetAction,
        openSignInModal,
        closeSignInModal,
        signInWithGoogle: handleGoogleSignIn,
        signOut: handleSignOut,
        loginAsDemoUser,
        clearError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
